import React, { useEffect, useState } from 'react';
import { Avatar, Box, Button, Chip, Paper, Rating, Snackbar, Alert, Stack, TextField, Typography } from '@mui/material';
import EnhancedErrorBoundary from '../components/EnhancedErrorBoundary.jsx';
import { ReviewCardSkeleton } from '../components/SkeletonLoaders.jsx';
import { apiFetch } from '../utils/apiFetch.js';

export default function MoverReviews({ token }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [reply, setReply] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const res = await apiFetch('/api/reviews/me', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        const reviewsData = data.reviews || data || [];
        setReviews(Array.isArray(reviewsData) ? reviewsData : []);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setReviews([]); // Ensure reviews is always an array
        setSnackbar({ open: true, message: 'Failed to load reviews.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchReviews();
    }
  }, [token]);

  const handleReply = (reviewId) => setReplyingTo(reviewId);
  const handleSendReply = async (reviewId) => {
    try {
      const replyRes = await apiFetch(`/api/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reply }),
      });
      if (!replyRes.ok) {
        const errorData = await replyRes.json().catch(() => ({}));
        throw new Error(errorData?.error || `HTTP ${replyRes.status}`);
      }
      setReply('');
      setReplyingTo(null);
      setSnackbar({ open: true, message: 'Reply sent!', severity: 'success' });
      // Refresh reviews
      const res = await apiFetch('/api/reviews/me', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const reviewsData = data.reviews || data || [];
      setReviews(Array.isArray(reviewsData) ? reviewsData : []);
    } catch (err) {
      console.error('Error sending reply:', err);
      setSnackbar({ open: true, message: 'Failed to send reply.', severity: 'error' });
    }
  };

  // Show skeleton while loading
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>Reviews</Typography>
        {[...Array(3)].map((_, i) => (
          <ReviewCardSkeleton key={i} />
        ))}
      </Box>
    );
  }

  return (
    <EnhancedErrorBoundary>
      <Box sx={{ 
        width: '100%',
        height: '100%',
        p: { xs: 2, md: 4 }, 
        bgcolor: '#F5F7FA',
        overflow: 'auto'
      }}>
        {/* Content wrapper with max width and centering */}
        <Box sx={{
          maxWidth: 1400,
          mx: 'auto',
          width: '100%'
        }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3, borderRadius: 3, color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #176B87 100%)', boxShadow: '0 12px 28px rgba(16,42,67,0.18)' }}>
          <Typography variant="overline" sx={{ color: '#9FE3D0', fontWeight: 800, letterSpacing: 1.5 }}>REPUTATION</Typography>
          <Typography variant="h4" sx={{ fontWeight: 850 }}>Reviews</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.76)', mt: 0.5 }}>Respond to customer feedback and keep your public profile current.</Typography>
        </Paper>
        {!Array.isArray(reviews) || reviews.length === 0 ? <Paper elevation={0} sx={{ p: 4, border: '1px solid #D9E2EC', borderRadius: 3, textAlign: 'center', color: 'text.secondary' }}>No reviews yet.</Paper> :
        reviews.map(r => (
          <Paper key={r._id} elevation={0} sx={{ mb: 2, p: 2.5, border: '1px solid #D9E2EC', borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: '#1E293B' }}>{(r.userId?.name || 'C').charAt(0)}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>{r.userId?.name || 'Customer'}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}</Typography>
                </Box>
              </Stack>
              <Chip label={`${r.rating || 0}/5`} color="warning" variant="outlined" />
            </Stack>
            <Rating value={Number(r.rating) || 0} readOnly size="small" sx={{ mt: 1.5 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>{r.comment}</Typography>
            
            {/* Show existing reply if it exists */}
            {r.reply && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E6EDF3' }}>
                <Typography variant="subtitle2" color="primary">Your Reply:</Typography>
                <Typography variant="body2">{r.reply}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.replyDate ? new Date(r.replyDate).toLocaleDateString() : ''}
                </Typography>
              </Box>
            )}
            
            {/* Reply form or reply button */}
            {replyingTo === r._id ? (
              <Box sx={{ mt: 1 }}>
                <TextField value={reply} onChange={e => setReply(e.target.value)} size="small" label="Reply" fullWidth multiline rows={2} sx={{ mb: 1 }} />
                <Button variant="contained" size="small" onClick={() => handleSendReply(r._id)}>Send</Button>
                <Button size="small" onClick={() => setReplyingTo(null)} sx={{ ml: 1 }}>Cancel</Button>
              </Box>
            ) : !r.reply && (
              <Button size="small" onClick={() => handleReply(r._id)} sx={{ mt: 1 }}>Reply</Button>
            )}
          </Paper>
        ))}
        <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
        </Box>
      </Box>
    </EnhancedErrorBoundary>
  );
}
